import { Html, Text } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AdditiveBlending,
  Color,
  Matrix4,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Quaternion,
  TubeGeometry,
  Vector3,
} from 'three'
import { createArcCurve, latLngToVector3 } from '../utils/geo'

type Place = {
  name: string
  lat: number
  lng: number
}

const EARTH_RADIUS = 3.75
const ARC_HEIGHT = 0.09125
const LINK_COLOR = new Color('#0685ed')

const places: [Place, Place] = [
  { name: 'Yumen, CN', lat: 39.803991, lng: 97.5548 },
  { name: 'Unalaska, USA', lat: 53.884592, lng: -166.55134 },
]

function GlowingMarker({ position }: { position: Vector3 }) {
  return (
    <group position={position.toArray()}>
      <mesh>
        <sphereGeometry args={[0.09, 16, 16]} />
        <meshBasicMaterial
          color={LINK_COLOR}
          transparent
          opacity={0.95}
          depthWrite={false}
          blending={AdditiveBlending}
        />
      </mesh>
      <mesh>
        <sphereGeometry args={[0.18, 16, 16]} />
        <meshBasicMaterial
          color={LINK_COLOR}
          transparent
          opacity={0.25}
          depthWrite={false}
          blending={AdditiveBlending}
        />
      </mesh>
    </group>
  )
}

function PulsingNode({ position }: { position: Vector3 }) {
  const meshRef = useRef<THREE.Mesh>(null)
  const materialRef = useRef<MeshStandardMaterial>(null)

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime()
    const pulse = (Math.sin(t * 2.2) + 1) / 2
    if (meshRef.current) {
      meshRef.current.scale.setScalar(0.9 + pulse * 0.35)
    }
    if (materialRef.current) {
      materialRef.current.emissiveIntensity = 1.2 + pulse * 1.6
    }
  })

  return (
    <mesh ref={meshRef} position={position.toArray()}>
      <sphereGeometry args={[0.05, 16, 16]} />
      <meshStandardMaterial
        ref={materialRef}
        color={LINK_COLOR}
        emissive={LINK_COLOR}
        emissiveIntensity={2}
        toneMapped={false}
      />
    </mesh>
  )
}

function PlaceLabel({ position, text }: { position: Vector3; text: string }) {
  const labelPos = useMemo(() => position.clone().multiplyScalar(1.04), [position])
  const [hidden, setHidden] = useState(false)
  const hiddenRef = useRef(false)
  const { camera } = useThree()

  useFrame(() => {
    const cameraPos = camera.position
    const toLabel = labelPos.clone().sub(cameraPos)
    const distanceToLabel = toLabel.length()
    const direction = toLabel.normalize()
    const projection = -cameraPos.dot(direction)

    let occluded = false
    if (projection > 0 && projection < distanceToLabel) {
      const closestPoint = cameraPos.clone().add(direction.multiplyScalar(projection))
      occluded = closestPoint.length() < EARTH_RADIUS
    }

    const nextHidden = occluded
    if (hiddenRef.current !== nextHidden) {
      hiddenRef.current = nextHidden
      setHidden(nextHidden)
    }
  })
  
  return (
    <group position={labelPos.toArray()}>
      <Html
        transform
        distanceFactor={12}
        sprite
        style={{ pointerEvents: 'none' }}
      >
        <div
          className="place-label"
          style={{
            display: hidden ? 'none' : 'block',
            backfaceVisibility: 'hidden',
            transformStyle: 'preserve-3d',
          }}
        >
          {text}
        </div>
      </Html>
    </group>
  )
}

function ArcText({
  curve,
  text,
}: {
  curve: THREE.Curve<Vector3>
  text: string
}) {
  // 💡 核心修复：坚决恢复你原本的救命补丁！防止引擎崩溃！
  const textMaterial = useMemo(() => {
    const mat = new MeshBasicMaterial({
      color: LINK_COLOR,
      transparent: true,
      opacity: 1,
      depthTest: false,
      depthWrite: false,
    })
    ;(mat as any).onBuild = () => {} 
    return mat
  }, [])

  const curveLength = useMemo(() => curve.getLength(), [curve])
  const fontSize = 0.22
  const spacing = fontSize * 0.62
  const totalWidth = spacing * (text.length - 1)
  const startOffset = (curveLength * 0.5) - totalWidth * 0.5
  const textOffset = 0.12

  return (
    <group>
        {text.split('').map((char, index) => {
          const distance = startOffset + spacing * index
          const u = distance / curveLength
          const position = curve.getPointAt(u)
          const tangent = curve.getTangentAt(u).normalize()
          const outward = position.clone().normalize()
          const textPosition = position.clone().add(outward.clone().multiplyScalar(textOffset))
          const side = new Vector3().crossVectors(tangent, outward).normalize()
          const up = new Vector3().crossVectors(side, tangent).normalize()
          const quaternion = new Quaternion().setFromRotationMatrix(
            new Matrix4().makeBasis(tangent, up, side)
          )

        return (
          <Text
            key={`${char}-${index}`}
            position={textPosition.toArray()}
            quaternion={quaternion}
            renderOrder={2}
            
            // 💡 核心修复 2：放弃阿里云跨域，直接读取 public 文件夹下的本地字体
            font={'/ManoloMono.otf'}
            
            
            fontSize={fontSize}
            color={LINK_COLOR.getStyle()}
            material={textMaterial}
            outlineWidth={0.005}
            outlineColor={'#3aa0ff'}
            anchorX="center"
            anchorY="middle"
          >
            {char}
          </Text>
        )
      })}
    </group>
  )
}

function GlowingArc({ start, end }: { start: Vector3; end: Vector3 }) {
  const flowRef = useRef<THREE.Mesh>(null)
  const { innerGeom, outerGeom, curve } = useMemo(() => {
    const arcCurve = createArcCurve(start, end, EARTH_RADIUS, ARC_HEIGHT)
    const tubularSegments = 80

    const innerRadius = 0.02
    const outerRadius = 0.05

    return {
      curve: arcCurve,
      innerGeom: new TubeGeometry(arcCurve, tubularSegments, innerRadius, 8, false),
      outerGeom: new TubeGeometry(arcCurve, tubularSegments, outerRadius, 8, false),
    }
  }, [start, end])

  const innerMat = useMemo(() => {
    return new MeshBasicMaterial({
      color: LINK_COLOR,
      transparent: true,
      opacity: 0.9,
      blending: AdditiveBlending,
      depthWrite: false,
    })
  }, [])

  const outerMat = useMemo(() => {
    return new MeshBasicMaterial({
      color: LINK_COLOR,
      transparent: true,
      opacity: 0.22,
      blending: AdditiveBlending,
      depthWrite: false,
    })
  }, [])

  useFrame(({ clock }) => {
    if (!flowRef.current) {
      return
    }
    const t = (clock.getElapsedTime() * 0.12) % 1
    const point = curve.getPoint(t)
    flowRef.current.position.copy(point)
  })

  return (
    <group>
      <mesh geometry={outerGeom} material={outerMat} />
      <mesh geometry={innerGeom} material={innerMat} />
      <ArcText curve={curve} text="6881.279958 km" />
      <mesh ref={flowRef}>
        <sphereGeometry args={[0.08, 12, 12]} />
        <meshBasicMaterial
          color={LINK_COLOR}
          transparent
          opacity={0.9}
          blending={AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
    </group>
  )
}

export default function Links() {
  const [a, b] = places

  const start = useMemo(() => latLngToVector3(a, EARTH_RADIUS * 1.01), [a])
  const end = useMemo(() => latLngToVector3(b, EARTH_RADIUS * 1.01), [b])

  return (
    <group>
      <GlowingArc start={start} end={end} />
      <GlowingMarker position={start} />
      <GlowingMarker position={end} />
      <PulsingNode position={start} />
      <PulsingNode position={end} />
      <PlaceLabel position={start} text={a.name} />
      <PlaceLabel position={end} text={b.name} />
    </group>
  )
}